import { useState } from 'react';
import { useSiteInfo } from '@contexts/SiteInfoContext';
import SiteMapping from '@configs/siteMapping';
import { useTranslation } from 'next-i18next';
import {
    useSoccerToday,
    useBasketballToday,
    useCricketToday,
} from './useMatchData';
import {
    sportList,
    TodayMatchSortedRule,
    CricketSortedRule,
    LiveScoreDisplayCount,
} from './liveScoreConstants';
import { LiveScoreCard, MatchScene } from './LiveScoreCard';
import EmptyMatch from './EmptyMatch';
import LiveScoreGroup from './LiveScoreGroup';
import PreLoader from '../common/PreLoader';
import ErrorBoundary from '../common/ErrorBoundary';

const TabContainer = ({ siteName, children }) => {
    return (
        {
            [SiteMapping.Nova88]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
            [SiteMapping.IBCBet]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
            [SiteMapping.PICA88]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
            [SiteMapping.id8899]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
            [SiteMapping.viva88]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--pills">{children}</div>
                </div>
            ),
            [SiteMapping.bong88]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--pills">{children}</div>
                </div>
            ),
            [SiteMapping.bettha88]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
            [SiteMapping.taraPlays]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
            [SiteMapping.fifabet]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
            [SiteMapping.ocean99]: (
                <div className="live-score__tab">
                    <div className="tabs tabs--underline">{children}</div>
                </div>
            ),
        }[siteName]
    );
};

const SoccerToday = ({ todaySelectedSport }) => {
    const { soccerToday, isLoading } = useSoccerToday();

    if (isLoading) {
        return <PreLoader />;
    }

    if (!soccerToday) {
        return <EmptyMatch />;
    }

    return (
        <LiveScoreGroup selectedSport={todaySelectedSport}>
            {soccerToday?.Result?.Match
                ?.filter((todayMatch) => (
                    todayMatch.HomeTeam.NameEn && todayMatch.AwayTeam.NameEn
                    && todayMatch.HomeTeam.Icon && todayMatch.AwayTeam.Icon))
                ?.slice(0, LiveScoreDisplayCount)
                ?.sort((a, b) => (
                    TodayMatchSortedRule.indexOf(a.Status) - TodayMatchSortedRule.indexOf(b.Status)
                ))
                ?.map((data, scoreIndex) => {
                    const key = `${data.MatchId}-${scoreIndex}`;
                    return (
                        <LiveScoreCard
                            key={key}
                            selectedSport={todaySelectedSport}
                            sportData={data}
                        />
                    );
                })}
        </LiveScoreGroup>
    );
};

const BasketballToday = ({ todaySelectedSport }) => {
    const { basketballToday, isLoading } = useBasketballToday();

    if (isLoading) {
        return <PreLoader />;
    }

    if (!basketballToday) {
        return <EmptyMatch />;
    }

    return (
        <LiveScoreGroup selectedSport={todaySelectedSport}>
            {
                basketballToday?.Result?.Match
                    ?.filter((todayMatch) => (
                        todayMatch.HomeTeam.NameEn && todayMatch.AwayTeam.NameEn
                        && todayMatch.HomeTeam.Icon && todayMatch.AwayTeam.Icon))
                    ?.slice(0, LiveScoreDisplayCount)
                    ?.sort((a, b) => (
                        TodayMatchSortedRule.indexOf(a.MatchStatus)
                    - TodayMatchSortedRule.indexOf(b.MatchStatus)
                    ))
                    ?.map((data, bIdx) => {
                        return (
                            <LiveScoreCard
                                key={`${data.MatchId}-${bIdx}`}
                                selectedSport={todaySelectedSport}
                                sportData={data}
                            />
                        );
                    })
            }
        </LiveScoreGroup>
    );
};
const TennisToday = ({ todaySelectedSport }) => {
    return [];
};
const CricketToday = ({ todaySelectedSport }) => {
    const { cricketToday, isLoading } = useCricketToday();

    if (isLoading) {
        return <PreLoader />;
    }

    if (!cricketToday) {
        return <EmptyMatch />;
    }

    return (
        <LiveScoreGroup selectedSport={todaySelectedSport}>
            {
                cricketToday?.Result?.Match
                    ?.filter((todayMatch) => (
                        todayMatch.HomeTeam.Name && todayMatch.AwayTeam.Name
                        && todayMatch.HomeTeam.Icon && todayMatch.AwayTeam.Icon))
                    ?.slice(0, LiveScoreDisplayCount)
                    ?.sort((a, b) => (
                        CricketSortedRule.indexOf(a.Status) - CricketSortedRule.indexOf(b.Status)
                    ))
                    ?.map((data, cricketIndex) => {
                        const key = `${data.MatchId}-${cricketIndex}`;
                        return (
                            <LiveScoreCard
                                key={key}
                                selectedSport={todaySelectedSport}
                                sportData={data}
                            />
                        );
                    })
            }
        </LiveScoreGroup>
    );
};

const LiveScoreToday = () => {
    const { siteName } = useSiteInfo();
    const [todaySelectedSport, setTodaySelectedSport] = useState('Soccer');
    const { t } = useTranslation();

    const scrollIntoViewWithOffset = (selector, offset) => {
        window.scrollTo({
            behavior: 'smooth',
            top:
            document.querySelector(selector).getBoundingClientRect().top
            - document.body.getBoundingClientRect().top - offset,
        });
    };

    return (
        <ErrorBoundary>
            <TabContainer siteName={siteName}>
                <div className="tabs__content">
                    {sportList.map(({ sportName, sportType, i18nKey }) => (
                        <div
                            key={sportName}
                            className="tab"
                            data-selected={todaySelectedSport === sportName}
                            onClick={() => {
                                scrollIntoViewWithOffset('.main__content', 68);
                                setTodaySelectedSport(sportName);
                            }}
                        >
                            <a className="btn">
                                <i className={`icon icon--sport${sportType}`} />
                                <span className="text">{t(i18nKey)}</span>
                            </a>
                        </div>
                    ))}
                </div>
            </TabContainer>
            {{
                Soccer: <SoccerToday todaySelectedSport={todaySelectedSport} />,
                Basketball: <BasketballToday todaySelectedSport={todaySelectedSport} />,
                Tennis: <TennisToday todaySelectedSport={todaySelectedSport} />,
                Cricket: <CricketToday todaySelectedSport={todaySelectedSport} />,
            }[todaySelectedSport]}
        </ErrorBoundary>
    );
};

export default LiveScoreToday;
